import { useState } from 'react';
import {
  createPuzzleTiles,
  getDailyOfflineActivity,
  getLaunchAnimalActivityPack,
  isPuzzleSolved,
  swapPuzzleTiles,
  type LaunchActivitySpecies,
} from './animalActivityPacks';

const GRID_SIZE = 3;

function tileLabel(tile: number): string {
  const row = Math.floor(tile / GRID_SIZE) + 1;
  const column = (tile % GRID_SIZE) + 1;
  return `picture piece from row ${row}, column ${column}`;
}

export function AnimalDailyActivities({
  animalName,
  speciesName,
  date,
}: {
  animalName: string;
  speciesName: LaunchActivitySpecies;
  date: string;
}) {
  const pack = getLaunchAnimalActivityPack(speciesName);
  const offlineActivity = getDailyOfflineActivity(speciesName, date);
  const [tiles, setTiles] = useState(() => createPuzzleTiles(speciesName, date));
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const solved = isPuzzleSolved(tiles);
  const status = solved
    ? `Puzzle complete! ${animalName} is ready for ${pack.puzzleCompletion}.`
    : selectedIndex === null
      ? 'Tap one picture piece, then tap another to swap them.'
      : 'Now tap another picture piece to swap it.';

  function selectTile(index: number) {
    if (solved) return;
    if (selectedIndex === null) {
      setSelectedIndex(index);
      return;
    }
    if (selectedIndex === index) {
      setSelectedIndex(null);
      return;
    }
    setTiles((currentTiles) => swapPuzzleTiles(currentTiles, selectedIndex, index));
    setSelectedIndex(null);
  }

  function reshuffle() {
    setTiles(createPuzzleTiles(speciesName, `${date}|again`));
    setSelectedIndex(null);
  }

  return (
    <div className="animal-activity-grid">
      <article className="learning-activity-card animal-puzzle-card">
        <p className="species-label">Tap-to-swap puzzle</p>
        <h4>Put {animalName}’s picture back together</h4>
        <p>
          Swap two pieces at a time to reveal {animalName} {pack.puzzleScene}.
        </p>
        <div className="animal-puzzle" role="group" aria-label={`${animalName} picture puzzle`}>
          {tiles.map((tile, index) => {
            const row = Math.floor(tile / GRID_SIZE);
            const column = tile % GRID_SIZE;
            return (
              <button
                key={`${tile}-${index}`}
                type="button"
                className={
                  selectedIndex === index ? 'animal-puzzle-tile is-selected' : 'animal-puzzle-tile'
                }
                aria-label={`${animalName} ${tileLabel(tile)}${
                  selectedIndex === index ? ', selected' : ''
                }`}
                aria-pressed={selectedIndex === index}
                disabled={solved}
                style={{
                  aspectRatio: pack.puzzleAspectRatio,
                  backgroundImage: `url("${pack.artUrl}")`,
                  backgroundPosition: `${column * 50}% ${row * 50}%`,
                }}
                onClick={() => selectTile(index)}
              />
            );
          })}
        </div>
        <p className="learning-feedback" role="status" aria-live="polite">
          {status}
        </p>
        {solved && (
          <button type="button" className="secondary-action puzzle-reshuffle" onClick={reshuffle}>
            Mix it up again
          </button>
        )}
      </article>

      <article className="learning-activity-card animal-offline-card">
        <p className="species-label">Away from the screen</p>
        <h4>{offlineActivity.title}</h4>
        <p>{offlineActivity.prompt}</p>
        <p className="learning-quiet-note">{offlineActivity.science}</p>
        <p className="learning-privacy-note">
          Do it together away from the screen. Nothing is recorded, saved, or sent.
        </p>
      </article>
    </div>
  );
}
